import { createStore, select, setProp, Store, withProps } from "@ngneat/elf";
import {
	addEntities,
	deleteEntitiesByPredicate,
	getAllEntitiesApply,
	getEntitiesCountByPredicate,
	getEntity,
	hasEntity,
	selectAllEntities,
	selectAllEntitiesApply,
	selectEntitiesCountByPredicate,
	selectManyByPredicate,
	setEntities,
	updateEntities,
	updateEntitiesByPredicate,
	withEntities,
} from "@ngneat/elf-entities";
import { Injectable } from "@angular/core";
import { Selectable } from "common/selection/state/selectable.model";
import { EMPTY, Observable, of } from "rxjs";
import { getIdIdentity, getTypeIdOfTypeIdString } from "common/selection/helper/selection-helper";

const selectionStore = createStore(
	{ name: "common-selection" },
	withEntities<Selectable, "identity">({
		idKey: "identity",
	}),
	withProps<{ isLoading: boolean }>({ isLoading: false }),
	withProps<{ selectAllPressed: boolean }>({ selectAllPressed: false }),
	withProps<{ deSelectAllPressed: boolean }>({ deSelectAllPressed: false }),
);

@Injectable({
	providedIn: "root",
})
export class SelectionRepository {
	private stores = new Map<string, Store>();

	isLoadingCommon$ = selectionStore.pipe(select((state) => state.isLoading));
	isSelectAllPressedCommon$ = selectionStore.pipe(select((state) => state.selectAllPressed));
	isDeselectAllPressedCommon$ = selectionStore.pipe(select((state) => state.deSelectAllPressed));
	addCommon(selectable: Selectable): void {
		if (!selectionStore.query(hasEntity(selectable.identity))) {
			selectionStore.update(addEntities(selectable));
		}
	}

	updateEntityCommon(selectable: Selectable, changes: Partial<Selectable>): void {
		selectionStore.update(updateEntities(selectable.identity, changes));
	}

	updateAllEntitiesCommon(storeIdentifier: string, shouldBeSelected: boolean): void {
		selectionStore.update(
			updateEntitiesByPredicate(
				(el: Selectable) =>
					(el.storeIdentifier === storeIdentifier && el.isSelected !== shouldBeSelected) || el.indeterminate === true,
				(entity) => ({
					...entity,
					isSelected: shouldBeSelected && entity.isSelectable,
					wasUpdated: entity.initiallySelected !== shouldBeSelected,
					indeterminate: false,
					selectedChildrenCount: null,
				}),
			),
		);
	}

	updateInitiallySelectedCommon(storeIdentifier: string): void {
		selectionStore.update(
			updateEntitiesByPredicate(
				(el: Selectable) => el.storeIdentifier === storeIdentifier,
				(entity) => ({
					...entity,
					isSelected: entity.initiallySelected,
				}),
			),
		);
	}

	addStoreDynamic(storeName: string): void {
		const selectionEntityStore = createStore(
			{ name: storeName },
			withEntities<Selectable, "identity">({ idKey: "identity" }),
		);

		this.stores.set(storeName, selectionEntityStore);
	}

	getEntityDynamic(storeName: string, key: string): Selectable | undefined {
		const store = this.getStoreDynamic(storeName);
		const typeId = getTypeIdOfTypeIdString(key);
		if (store && typeId) {
			const identity = getIdIdentity(storeName, typeId);
			// TODO use this.getEntity ...
			return store.query(getEntity(identity));
		}
		return undefined;
	}

	// TODO use identity instead of typeId + storeIdentifier ?!
	getEntityCommon(key: string, storeIdentifier: string): Selectable | undefined {
		const foundEntities = selectionStore.query(
			getAllEntitiesApply({
				filterEntity: (e: Selectable) => e.typeId === key && e.storeIdentifier === storeIdentifier,
			}),
		);
		return foundEntities[0];
	}

	getStoreDynamic(storeName: string): Store | undefined {
		if (this.hasStateDynamic(storeName)) {
			return this.stores.get(storeName);
		}
		return undefined;
	}

	hasStateDynamic(storeName: string): boolean {
		if (this.stores.get(storeName) !== undefined) {
			return true;
		}
		return false;
	}

	setLoadingCommon(loading: boolean): void {
		selectionStore.update(setProp("isLoading", loading));
	}

	setLoadingDynamic(storeName: string, loading: boolean): void {
		const store = this.getStoreDynamic(storeName);
		if (store) {
			store.update(setProp("isLoading", loading));
		}
	}

	getLoadingDynamic(storeName: string): Observable<boolean> | undefined {
		const store = this.getStoreDynamic(storeName);
		if (store) {
			return store.pipe(select((state) => state.isLoading));
		}
		return undefined;
	}

	updateDynamic(storeName: string, selectables: Selectable[]): void {
		const store = this.getStoreDynamic(storeName);
		if (store) {
			store.update(setEntities(selectables));
		}
	}

	updateEntityDynamic(selectable: Selectable, changes: Partial<Selectable>): void {
		const store = this.getStoreDynamic(selectable.storeIdentifier);
		if (store) {
			store.update(updateEntities(selectable.identity, changes));
		}
	}

	resetDynamic(storeName: string): void {
		const store = this.getStoreDynamic(storeName);
		if (store) {
			store.reset();
		}
	}

	resetCommon(): void {
		selectionStore.reset();
		selectionStore.update(setProp("selectAllPressed", false));
	}

	deleteDynamic(storeName: string): void {
		const store = this.getStoreDynamic(storeName);
		if (store) {
			store.destroy();
			// remove from map
			this.stores.delete(storeName);
		}
	}

	setChildrenSelectedDynamic(storeName: string, target: Selectable, leavesOnly: boolean): void {
		const store = this.getStoreDynamic(storeName);
		if (store) {
			if (leavesOnly) {
				store.update(
					updateEntitiesByPredicate(
						(el: Selectable) =>
							el.parentId === target.id && el.type === target.type && !this.hasChildrenDynamic(storeName, el),
						{
							isSelected: true,
						},
					),
				);
			} else {
				store.update(
					updateEntitiesByPredicate((el: Selectable) => el.parentId === target.id && el.type === target.type, {
						isSelected: true,
					}),
				);
			}
		}
	}

	setChildrenDeSelectedDynamic(storeName: string, target: Selectable): void {
		const store = this.getStoreDynamic(storeName);
		if (store) {
			store.update(
				updateEntitiesByPredicate((el: Selectable) => el.parentId === target.id && el.type === target.type, {
					isSelected: false,
				}),
			);
		}
	}

	private hasChildrenDynamic(storeName: string, selectable: Selectable): boolean {
		const directChildren = this.getAllDirectChildrenDynamic(storeName, selectable);
		return directChildren !== undefined && directChildren.length > 0;
	}

	getAllDirectChildrenDynamic(storeName: string, parent: Selectable): Selectable[] | undefined {
		const store = this.getStoreDynamic(storeName);
		if (store) {
			return store.query(
				getAllEntitiesApply({
					filterEntity: (e: Selectable) =>
						e.parentId === parent.id && e.type.toLocaleLowerCase() === parent.type.toLocaleLowerCase(),
				}),
			);
		}
		return undefined;
	}

	getAllDirectChildrenCommon(parent: Selectable): Selectable[] | undefined {
		return selectionStore.query(
			getAllEntitiesApply({
				filterEntity: (e: Selectable) =>
					e.parentId === parent.id && e.parentType === parent.type && e.storeIdentifier === parent.storeIdentifier,
			}),
		);
	}

	getAllFromDynamic$(storeName: string): Observable<Selectable[]> | undefined {
		const store = this.getStoreDynamic(storeName);
		if (store) {
			return store.pipe(selectAllEntities());
		}
		return undefined;
	}

	getAllSelectedFromDynamic$(storeName: string): Observable<Selectable[]> | undefined {
		const store = this.getStoreDynamic(storeName);
		if (store) {
			return store.pipe(
				selectAllEntitiesApply({
					filterEntity: (e: Selectable) => e.isSelected,
				}),
			);
		}
		return undefined;
	}

	hasParentCommon(identifier: string): boolean {
		return selectionStore.query(getEntitiesCountByPredicate((e: Selectable) => e.storeIdentifier === identifier)) > 0;
	}

	getSelectedChildrenCountFromCommon(parentEntity: Selectable): number | undefined {
		if (selectionStore) {
			return selectionStore.query(
				getEntitiesCountByPredicate(
					(e: Selectable) =>
						e.parentId === parentEntity.id &&
						e.parentType === parentEntity.type &&
						parentEntity.storeIdentifier === e.storeIdentifier &&
						e.isSelected,
				),
			);
		}
		return undefined;
	}

	getDeSelectedChildrenCountFromCommon(parent: Selectable): number | undefined {
		if (selectionStore) {
			return selectionStore.query(
				getEntitiesCountByPredicate(
					(e: Selectable) =>
						e.parentId === parent.id &&
						e.parentType === parent.type &&
						e.storeIdentifier === parent.storeIdentifier &&
						!e.isSelected,
				),
			);
		}
		return undefined;
	}

	getDeSelectedCountByIdentifierCommon$(identifier: string): Observable<number> {
		if (selectionStore) {
			return selectionStore.pipe(
				selectEntitiesCountByPredicate(
					(e: Selectable) => e.storeIdentifier === identifier && !e.isSelected && e.isSelectable,
				),
			);
		}
		return of(0);
	}

	getSelectedCountByIdentifierCommon$(identifier: string): Observable<number> {
		if (selectionStore) {
			return selectionStore.pipe(
				selectEntitiesCountByPredicate(
					(e: Selectable) => e.storeIdentifier === identifier && e.isSelected && e.isSelectable,
				),
			);
		}
		return of(0);
	}

	getSelectableCountByIdentifierCommon$(identifier: string): Observable<number> {
		if (selectionStore) {
			return selectionStore.pipe(
				selectEntitiesCountByPredicate((e: Selectable) => e.storeIdentifier === identifier && e.isSelectable),
			);
		}
		return of(0);
	}

	/**
	 * @param identifier  string of the store identifier.
	 */
	getEntitiesByStoreNameCommon$(identifier: string, parentType?: string): Observable<Selectable[]> {
		if (selectionStore) {
			if (parentType) {
				return selectionStore.pipe(
					selectManyByPredicate((e: Selectable) => e.storeIdentifier === identifier && e.parentType === parentType),
				);
			}
			return selectionStore.pipe(selectManyByPredicate((e: Selectable) => e.storeIdentifier === identifier));
		}
		return EMPTY;
	}

	/**
	 * @param identifier  string of the store identifier.
	 */
	getIndeterminateOrSelectedByStoreCommon$(storeIdentifier: string, type?: string): Observable<Selectable[]> {
		if (selectionStore) {
			if (type) {
				return selectionStore.pipe(
					selectManyByPredicate(
						(e: Selectable) =>
							(!!e.indeterminate || !!e.isSelected) &&
							!!e.storeIdentifier &&
							e.storeIdentifier === storeIdentifier &&
							e.type === type,
					),
				);
			}
			return selectionStore.pipe(
				selectManyByPredicate(
					(e: Selectable) =>
						(!!e.indeterminate || !!e.isSelected) && !!e.storeIdentifier && e.storeIdentifier === storeIdentifier,
				),
			);
		}
		return EMPTY;
	}

	getAllSelectedCommon$(storeIdentifier: string): Observable<Selectable[]> {
		if (selectionStore) {
			return selectionStore.pipe(
				selectManyByPredicate(
					(element: Selectable) => element.isSelected && element.storeIdentifier === storeIdentifier,
				),
			);
		}
		return EMPTY;
	}

	getAllNotSelectedFromCommon$(storeIdentifier: string): Observable<Selectable[]> {
		if (selectionStore) {
			return selectionStore.pipe(
				selectManyByPredicate(
					(element: Selectable) =>
						!element.isSelected && element.isSelectable && element.storeIdentifier === storeIdentifier,
				),
			);
		}
		return EMPTY;
	}

	getAllUpdatedCommon$(storeIdentifier: string): Observable<Selectable[]> {
		if (selectionStore) {
			return selectionStore.pipe(
				selectManyByPredicate(
					(element: Selectable) => element.wasUpdated && element.storeIdentifier === storeIdentifier,
				),
			);
		}
		return EMPTY;
	}

	getAllAddedCommon$(storeIdentifier: string): Observable<Selectable[]> {
		if (selectionStore) {
			return selectionStore.pipe(
				selectManyByPredicate(
					(element: Selectable) =>
						element.isSelected && !element.initiallySelected && element.storeIdentifier === storeIdentifier,
				),
			);
		}
		return EMPTY;
	}

	getAllAddedCountCommon$(storeIdentifier: string): Observable<number> {
		if (selectionStore) {
			return selectionStore.pipe(
				selectEntitiesCountByPredicate(
					(element: Selectable) =>
						element.isSelected && !element.initiallySelected && element.storeIdentifier === storeIdentifier,
				),
			);
		}
		return of(0);
	}

	getAllRemovedCommon$(storeIdentifier: string): Observable<Selectable[]> {
		if (selectionStore) {
			return selectionStore.pipe(
				selectManyByPredicate(
					(element: Selectable) =>
						!element.isSelected && element.initiallySelected && element.storeIdentifier === storeIdentifier,
				),
			);
		}
		return EMPTY;
	}

	getAllRemovedCountCommon$(storeIdentifier: string): Observable<number> {
		if (selectionStore) {
			return selectionStore.pipe(
				selectEntitiesCountByPredicate(
					(element: Selectable) =>
						!element.isSelected && element.initiallySelected && element.storeIdentifier === storeIdentifier,
				),
			);
		}
		return of(0);
	}

	/**
	 * @param identifier  string of the store identifier.
	 */
	getSelectedByStoreCommon(storeIdentifier: string, type?: string): Selectable[] {
		if (selectionStore) {
			if (type) {
				return selectionStore.query(
					getAllEntitiesApply({
						filterEntity: (e: Selectable) =>
							e.isSelected && !!e.storeIdentifier && e.storeIdentifier === storeIdentifier && e.type === type,
					}),
				);
			}
			return selectionStore.query(
				getAllEntitiesApply({
					filterEntity: (e: Selectable) => e.isSelected && !!e.storeIdentifier && e.storeIdentifier === storeIdentifier,
				}),
			);
		}
		return [];
	}

	getAllSelectedCountDynamic$(storeName: string): Observable<number> | undefined {
		const store = this.getStoreDynamic(storeName);
		if (store) {
			return store.pipe(selectEntitiesCountByPredicate((e: Selectable) => e.isSelected));
		}
		return undefined;
	}

	getAllAddedCountDynamic$(storeName: string): Observable<number> | undefined {
		const store = this.getStoreDynamic(storeName);
		if (store) {
			return store.pipe(
				selectEntitiesCountByPredicate((element: Selectable) => element.isSelected && !element.initiallySelected),
			);
		}
		return undefined;
	}

	getAllAddedDynamic$(storeName: string): Observable<Selectable[]> | undefined {
		const store = this.getStoreDynamic(storeName);
		if (store) {
			return store.pipe(
				selectAllEntitiesApply({
					filterEntity: (element: Selectable) => element.isSelected && !element.initiallySelected,
				}),
			);
		}
		return undefined;
	}

	getAllRemovedCountDynamic$(storeName: string): Observable<number> | undefined {
		const store = this.getStoreDynamic(storeName);
		if (store) {
			return store.pipe(
				selectEntitiesCountByPredicate((element: Selectable) => !element.isSelected && element.initiallySelected),
			);
		}
		return undefined;
	}

	getAllRemovedDynamic$(storeName: string): Observable<Selectable[]> | undefined {
		const store = this.getStoreDynamic(storeName);
		if (store) {
			return store.pipe(
				selectAllEntitiesApply({
					filterEntity: (element: Selectable) => !element.isSelected && element.initiallySelected,
				}),
			);
		}
		return undefined;
	}

	getAllUpdatedDynamic$(storeName: string): Observable<Selectable[]> | undefined {
		const store = this.getStoreDynamic(storeName);
		if (store) {
			return store.pipe(
				selectAllEntitiesApply({
					filterEntity: (element: Selectable) => element.wasUpdated,
				}),
			);
		}
		return undefined;
	}

	removeAllCommon(storeIdentifier: string): void {
		selectionStore.update(
			deleteEntitiesByPredicate((el: Selectable) => !!el.storeIdentifier && el.storeIdentifier === storeIdentifier),
		);
	}

	setSelectAllPressedCommon(isSelectAllPressed: boolean): void {
		selectionStore.update(setProp("selectAllPressed", isSelectAllPressed));
	}

	setDeselectAllPressedCommon(isDeselectAllPressed: boolean): void {
		selectionStore.update(setProp("deSelectAllPressed", isDeselectAllPressed));
	}
}
